import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import type { PossessionCategory } from '../constants/possessions';

export type AcquisitionPriority = 'low' | 'medium' | 'high';

/**
 * An "Acquisition" — a thing the user is planning to get (a new phone, car,
 * clothes, furniture…). Listing it offloads the mental load; marking it acquired
 * is the follow-through. Both feed the derived Ecological "Acquisitions" area XP
 * (utils/acquisitionsXp). Reuses PossessionCategory (same kinds of things).
 */
export interface AcquisitionItem {
  id: string;
  name: string;
  category: PossessionCategory;
  notes?: string;
  /** Optional budget/target price. */
  targetPrice?: number;
  priority: AcquisitionPriority;
  acquired: boolean;
  acquiredAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Supabase sync — acquisitions as a blob per user (whole-store-blob pattern) ──
const USER_ACQUISITIONS_TABLE = 'user_acquisitions';
function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

function readCloudItems(data: any) {
  return Array.isArray(data?.items) ? data.items.map(mapItem) : [];
}

// ⚠️ Read-modify-write, NOT a wholesale upsert. The old code PUT the entire local
// array, so a device holding a stale cache silently deleted records it had never
// seen (diagnosed 2026-07-27 on the inbox — see lib/syncBlob.ts).
const blobSync = createBlobSync({
  transport: supabaseBlobTransport(USER_ACQUISITIONS_TABLE),
  owner: () => useAcquisitionsStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync acquisitions to Supabase:', err),
  build: (cloud) => {
    const state = useAcquisitionsStore.getState();
    const cloudTombs = readTombstones(cloud);
    const deleted = pruneTombstones(mergeTombstones(state.deleted, cloudTombs));
    const items = mergeCollection(state.items, readCloudItems(cloud), deleted);
    if (matchesCloud([[items, cloud?.items]], deleted, cloudTombs)) return null;
    return { items, deleted };
  },
});

interface AcquisitionsState {
  items: AcquisitionItem[];
  /** id → deletedAt. Without this, the union-on-write would resurrect deletes. */
  deleted: Tombstones;
  initializedUserId: string | null;
  /**
   * The last load failed — Growth Foundations says "couldn't check" (and offers Retry) instead of
   * "checking". Not saved: a reload loads again.
   */
  loadFailed: boolean;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  addItem: (input: { name: string; category: PossessionCategory; priority?: AcquisitionPriority; targetPrice?: number; notes?: string }) => AcquisitionItem;
  updateItem: (id: string, updates: Partial<Pick<AcquisitionItem, 'name' | 'category' | 'priority' | 'targetPrice' | 'notes'>>) => void;
  deleteItem: (id: string) => void;
  /** Toggle the acquired flag (the follow-through / dopamine moment). */
  setAcquired: (id: string, acquired: boolean) => void;
}

const now = () => new Date().toISOString();

function mapItem(i: any): AcquisitionItem {
  return {
    id: i.id,
    name: i.name,
    category: i.category ?? 'other',
    notes: i.notes,
    targetPrice: typeof i.targetPrice === 'number' ? i.targetPrice : undefined,
    priority: i.priority ?? 'medium',
    acquired: !!i.acquired,
    acquiredAt: i.acquiredAt ?? null,
    createdAt: i.createdAt ?? now(),
    updatedAt: i.updatedAt ?? now(),
  };
}

export const useAcquisitionsStore = create<AcquisitionsState>()(
  persist<AcquisitionsState, [], [], any>(
    (set, get) => ({
      items: [],
      deleted: {},
      initializedUserId: null,
      loadFailed: false,
      cachedUserId: null,

      initialize: () => dedupeInit('user_acquisitions', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          if (get().loadFailed) set({ loadFailed: false });
          if (get().cachedUserId === user.id && isCloudFresh('user_acquisitions', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }
          const { data, error } = await supabase.from(USER_ACQUISITIONS_TABLE).select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;
          const row = (data ?? [])[0];
          set((state) => {
            const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
            if (row && row.data && Array.isArray(row.data.items)) {
              const deleted = pruneTombstones(mergeTombstones(
                cacheIsOurs ? state.deleted : {}, readTombstones(row.data)));
              const merged = mergeCollection(
                cacheIsOurs ? state.items : [], readCloudItems(row.data), deleted);
              return {
                // Same reference when the merge was a no-op, so the subscription
                // below doesn't fire a pointless write on every boot.
                items: sameRecords(merged, state.items) ? state.items : merged,
                deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            return cacheIsOurs
              ? { initializedUserId: user.id, cachedUserId: user.id }
              : { items: [], deleted: {}, initializedUserId: user.id, cachedUserId: user.id };
          });
          blobSync.writeBack(row?.data);
          markCloudFresh('user_acquisitions', user.id);
        } catch (error) {
          console.error('Failed to initialize acquisitions from Supabase:', error);
          // ⚠️ Only this account's: a load that started before a sign-out says nothing about the next.
          if (at === accountGeneration()) set({ loadFailed: true });
        }
      }),

      addItem: ({ name, category, priority, targetPrice, notes }) => {
        const item: AcquisitionItem = {
          id: crypto.randomUUID(),
          name: name.trim(),
          category,
          notes: notes?.trim() || undefined,
          targetPrice: typeof targetPrice === 'number' && targetPrice > 0 ? targetPrice : undefined,
          priority: priority ?? 'medium',
          acquired: false,
          acquiredAt: null,
          createdAt: now(),
          updatedAt: now(),
        };
        set(state => ({ items: [...state.items, item] }));
        return item;
      },

      updateItem: (id, updates) => {
        set(state => ({
          items: state.items.map(i =>
            i.id === id ? { ...i, ...updates, updatedAt: now() } : i,
          ),
        }));
      },

      deleteItem: (id) => {
        // ⚠️ Must leave a tombstone — writes are a union now, so a bare filter
        // would be undone by the next merge against the cloud copy.
        set(state => ({
          items: state.items.filter(i => i.id !== id),
          deleted: tombstone(state.deleted, id),
        }));
      },

      setAcquired: (id, acquired) => {
        set(state => ({
          items: state.items.map(i =>
            i.id === id ? { ...i, acquired, acquiredAt: acquired ? now() : null, updatedAt: now() } : i,
          ),
        }));
      },
    }),
    {
      name: 'acquisitions-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (state: AcquisitionsState) =>
        ({ items: state.items, deleted: state.deleted, cachedUserId: state.cachedUserId }) as any,
    },
  ),
);

// Auto-sync on change. No suppression flag: initialize() returns the SAME array
// reference when its merge changed nothing, and when it did change something a
// write-back is exactly what makes the two sides converge.
useAcquisitionsStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (state.items === prev.items && state.deleted === prev.deleted) return;
  blobSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useAcquisitionsStore);
